import 'react-native-url-polyfill/auto';
import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import * as SecureStore from 'expo-secure-store';
import { AppState, Platform } from 'react-native';

import { SUPABASE_ANON_KEY, SUPABASE_URL, isSupabaseConfigured } from '@/config';

import type { Database } from './types';

/**
 * Session tokens live in the encrypted keychain/keystore on native
 * (expo-secure-store), and in localStorage on web (SecureStore has no web
 * implementation; the anon key itself is public by design - RLS is the
 * security boundary).
 */
const secureStoreAdapter = {
  getItem: (key: string) => SecureStore.getItemAsync(key),
  setItem: (key: string, value: string) => SecureStore.setItemAsync(key, value),
  removeItem: (key: string) => SecureStore.deleteItemAsync(key),
};

function buildClient(): SupabaseClient<Database> | null {
  if (!isSupabaseConfigured || !SUPABASE_URL || !SUPABASE_ANON_KEY) {
    return null;
  }
  const client = createClient<Database>(SUPABASE_URL, SUPABASE_ANON_KEY, {
    auth: {
      ...(Platform.OS !== 'web' ? { storage: secureStoreAdapter } : {}),
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: Platform.OS === 'web',
    },
  });

  // Pause/resume token refresh with the app lifecycle (Supabase RN guidance).
  if (Platform.OS !== 'web') {
    AppState.addEventListener('change', (state) => {
      if (state === 'active') {
        client.auth.startAutoRefresh();
      } else {
        client.auth.stopAutoRefresh();
      }
    });
  }
  return client;
}

/** Null when env is missing - screens render the "backend not connected" state. */
export const supabase = buildClient();

export class SupabaseNotConfiguredError extends Error {
  constructor() {
    super('Supabase is not configured (missing EXPO_PUBLIC_SUPABASE_URL / ANON_KEY)');
    this.name = 'SupabaseNotConfiguredError';
  }
}

/** For code paths that must not run unconfigured (mutations, auth). */
export function requireSupabase(): SupabaseClient<Database> {
  if (!supabase) throw new SupabaseNotConfiguredError();
  return supabase;
}
